import React, {
  memo,
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
} from "react";
import { Container } from "@pixi/react";
// import * as PIXI from "pixi.js";
// import anime from "animejs";
import HoldAndWinOverlayCell from "./HoldAndWinOverlayCell";
import { CellAnticipationEffect } from "./cellAnticipation";
import { HOLD_AND_WIN_COLUMNS, HOLD_AND_WIN_ROWS } from "./HoldAndWinBoard";
// import { toDataRow } from "./chestCollection";
// import { toDataRow } from "./chestCollection";
import { isScreenRowOpen } from "../../RGS/Providers/PopiPlay/holdAndWinState";

// ── The locked rows (GDD "Locked-Row Symbols": "Show dormant symbols through
// a locked overlay until activation") ──
// NOW the room's locked_reel plates (HoldAndWinGameViewController
// .attachLockedRows, 2026-09-30) — the bars below are commented out.
// A black bar over each locked row — a stand-in until the animator's lock art
// fills the room's attachment_locked_reel_1..4 slots (Vladimir, 2026-09-29:
// "a black overlay with like 0.75 opacity").
// const LOCKED_ALPHA = 0.75;
// Its fade when the row opens. 250 (the win dim's undim) was over before it
// could be seen (Vladimir, 2026-09-29).
// const UNLOCK_FADE_MS = 250;
// const UNLOCK_FADE_MS = 500;
// Back to short — it no longer holds the round (non-blocking, with the coin
// counter): "short fade and done" (Vladimir, 2026-09-29).
// const UNLOCK_FADE_MS = 250;

/**
 * HoldAndWinOverlay — the live spines over the Hold & Win board.
 *
 * Mounted in the room's FRONT slot, laid out exactly like HoldAndWinBoard
 * (same cells, same centring), so a spine sits on its cell. Above the frame
 * and outside the window clip, so a landing's flames can reach past the cell.
 *
 * Imperative API (ref): play(col, row, symbolId, value), clear(col, row),
 * clearAll(), collect(col, row, value, instant), setValue(col, row, value),
 * cellAnchor(col, row) (the cell's centre, for trails), trailLayer() (above
 * every cell), cellHeight(). Cells are
 * [column][row], row 0 = the top screen row.
 */
// The draw order inside the overlay: locked rows' cells, the lock plates,
// open rows' cells, the trails (1000).
const CELL_Z_LOCKED = 0;
const PLATES_Z = 10;
const CELL_Z_OPEN = 20;
// ── A plate playing its `out` (2026-10-14) ── the clip grows the plate to
// 1.3x as it fades, over the open row next to it: there the open row's coins
// (20 + importance 9 + reveal 5 = up to 34) came through it. For its `out`
// the plate draws above every cell, under the anticipation and the trails.
const PLATES_OPENING_Z = 50;
// The last cells' anticipation (cellAnticipation.js) — over every cell and
// the plates, under the trails.
const ANTICIPATION_Z = 500;
// The rigs every cell builds ahead (HoldAndWinOverlayCell.prebuild) and how
// many cells a frame — 40 cells x 4 rigs over ~20 frames.
const PREBUILD_RIGS = ["c", "cj", "ch", "scr"];
const PREBUILD_CELLS_PER_FRAME = 2;

// `getPixelCellHeight`: the cell's height in SCREEN pixels, for the fire
// trails (coinToLogoTrail sizes by it and divides the layer's scale back out).
// The overlay itself works in the room spine's units.
const HoldAndWinOverlay = forwardRef(
  ({ cellWidth, cellHeight, getPixelCellHeight }, ref) => {
  const containerRefs = useRef(
    Array.from({ length: HOLD_AND_WIN_COLUMNS }, () =>
      Array.from({ length: HOLD_AND_WIN_ROWS }, () => React.createRef()),
    ),
  );
  const cells = useRef(null);
  // The chest collections' fire trails fly here — above every cell.
  const trailLayerRef = useRef(null);
  // ── The lock plates, BETWEEN the cells (2026-10-01) ── the room's
  // locked_reel plates are moved in here (HoldAndWinGameViewController.
  // setPlatesHost), so each cell's zIndex can put it under them (a row still
  // locked) or over them (an open row — its coins poke up into the plate
  // above). Vladimir: "i just want unlocked symbols to be on top of them".
  const platesLayerRef = useRef(null);
  // A plate playing its `out` (PLATES_OPENING_Z).
  const platesOpeningLayerRef = useRef(null);
  // The last cells' anticipation — one effect, one cell at a time.
  const anticipationLayerRef = useRef(null);
  const anticipation = useRef(null);
  // One black bar per screen row, above the cells (locked rows only show).
  // const lockLayerRef = useRef(null);
  // const lockBars = useRef([]);
  // const unlockedRef = useRef(4);

  // One overlay cell per board cell, built once.
  useEffect(() => {
    cells.current = containerRefs.current.map((column) =>
      column.map((r) => (r.current ? new HoldAndWinOverlayCell(r.current) : null)),
    );
    // The cell size is fixed (native units): set once, so the prebuilt rigs
    // below are scaled like the ones a landing shows.
    cells.current.forEach((column) =>
      column.forEach((c) => c?.setCellHeight(cellHeight)),
    );
    anticipation.current = new CellAnticipationEffect(anticipationLayerRef.current);
    // Built and on the GPU now — not in the frame its first chain starts.
    anticipation.current.prepare();
    // ── Every cell's rigs, built ahead (2026-10-07) ── a coin / jackpot coin
    // / chest / scarab spine was built the first time it landed in a cell,
    // mid-respin. A few cells a frame from the mount (the entry wipe), done
    // well before the first respin lands; a cell gone by its turn is skipped.
    const prebuildQueue = (cells.current || []).flat().filter(Boolean);
    let prebuildFrame = null;
    const prebuildNext = () => {
      prebuildFrame = null;
      for (let n = 0; n < PREBUILD_CELLS_PER_FRAME && prebuildQueue.length; n++) {
        prebuildQueue.shift().prebuild(PREBUILD_RIGS);
      }
      if (prebuildQueue.length) prebuildFrame = requestAnimationFrame(prebuildNext);
    };
    prebuildFrame = requestAnimationFrame(prebuildNext);
    return () => {
      if (prebuildFrame) cancelAnimationFrame(prebuildFrame);
      prebuildQueue.length = 0;
      cells.current?.forEach((column) => column.forEach((c) => c?.destroy()));
      cells.current = null;
      anticipation.current?.destroy();
      anticipation.current = null;
    };
    // Built once; cellHeight is a constant (native units).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The lock bars — built once, redrawn to the cell size.
  // useEffect(() => {
    // const layer = lockLayerRef.current;
    // if (!layer) return undefined;
    // if (!lockBars.current.length) {
      // lockBars.current = Array.from({ length: HOLD_AND_WIN_ROWS }, (_, screenRow) => {
        // const bar = new PIXI.Graphics();
        // // Built already showing the rows last set (setUnlockedRows may have
        // // run before the bars existed — they would have stayed invisible).
        // bar.alpha = toDataRow(screenRow) >= unlockedRef.current ? LOCKED_ALPHA : 0;
        // bar.eventMode = "none";
        // layer.addChild(bar);
        // return bar;
      // });
    // }
    // const width = HOLD_AND_WIN_COLUMNS * cellWidth;
    // const x0 = -width / 2;
    // const y0 = -(HOLD_AND_WIN_ROWS / 2) * cellHeight;
    // lockBars.current.forEach((bar, screenRow) => {
      // bar.clear();
      // bar.beginFill(0x000000, 1);
      // bar.drawRect(x0, y0 + screenRow * cellHeight, width, cellHeight);
      // bar.endFill();
    // });
    // return undefined;
  // }, [cellWidth, cellHeight]);

  // useEffect(
    // () => () => {
      // lockBars.current.forEach((bar) => {
        // anime.remove(bar);
        // // The lock layer's own unmount has usually destroyed its children
        // // already — a second destroy() throws (null geometry) and took the
        // // whole tree down on the room's exit.
        // if (!bar.destroyed) bar.destroy();
      // });
      // lockBars.current = [];
    // },
    // [],
  // );

  useImperativeHandle(
    ref,
    () => ({
      play: (col, row, symbolId, value, skin = null) => {
        const cell = cells.current?.[col]?.[row];
        if (!cell) return false;
        cell.setCellHeight(cellHeight);
        return cell.play(symbolId, value, skin);
      },
      clear: (col, row) => cells.current?.[col]?.[row]?.clear(),
      clearAll: () =>
        cells.current?.forEach((column) => column.forEach((c) => c?.clear())),
      collect: (col, row, value, instant = false) =>
        cells.current?.[col]?.[row]?.collect(value, instant) ?? Promise.resolve(),
      // The Scarab reveal (scarabReveal.js) — see the cell's methods.
      rig: (col, row) => cells.current?.[col]?.[row]?.rig?.() ?? null,
      // A chest's silver / gold (its collect sound, chestCollection).
      skin: (col, row) => cells.current?.[col]?.[row]?.skin?.() ?? null,
      playWin: (col, row) =>
        cells.current?.[col]?.[row]?.playWin?.() ?? Promise.resolve(),
      // A coin a scarab multiplied: its anticipation clips, quick.
      playMultiplied: (col, row) =>
        cells.current?.[col]?.[row]?.playMultiplied?.() ?? Promise.resolve(),
      revealScarab: (col, row, multiplier) =>
        cells.current?.[col]?.[row]?.revealScarab?.(multiplier) ?? Promise.resolve(),
      showScarabMultiplier: (col, row, multiplier) =>
        cells.current?.[col]?.[row]?.showMultiplier?.(multiplier),
      // revealJackpotValue: (col, row, value) =>
      //   cells.current?.[col]?.[row]?.revealJackpotValue?.(value) ?? Promise.resolve(),
      revealJackpotValue: (col, row, value, options) =>
        cells.current?.[col]?.[row]?.revealJackpotValue?.(value, options) ??
        Promise.resolve(),
      // A jackpot coin still on its tier word (revealHiddenJackpots).
      isHiddenJackpot: (col, row) => !!cells.current?.[col]?.[row]?.isHiddenJackpot?.(),
      // The style its number is drawn in — a trail carries it in the same one.
      valueStyle: (col, row) => cells.current?.[col]?.[row]?.valueStyle?.() ?? "coinValue",
      // One full pass of the cell's idle loop (cell.idleLoopEnd).
      idleLoopEnd: (col, row) =>
        cells.current?.[col]?.[row]?.idleLoopEnd?.() ?? Promise.resolve(),
      cellAnchor: (col, row) => containerRefs.current[col]?.[row]?.current ?? null,
      setValue: (col, row, value) => cells.current?.[col]?.[row]?.setValue(value),
      darken: (col, row) => cells.current?.[col]?.[row]?.darken(),
      // A max win's leftovers (PO, 2026-11-03): every standing symbol NOT in
      // `collected` ("col_row") loses its number and plays the darker
      // `darken_max` — all at once.
      darkenUncollected: (collected) =>
        cells.current?.forEach((column, col) =>
          column.forEach((cell, row) => {
            if (!cell?.active || collected?.has(`${col}_${row}`)) return;
            // // A scarab went dark after its reveal and has no number — the
            // // clip starts bright, so it would flash back up.
            // if (cell.active === cell.rigs?.get("scr")) return;
            // ...in too (PO, 2026-11-03: everything not included dims): its
            // `darken_max` starts at its `darken`'s end (scr.json), no flash.
            // cell.setValue(0);
            // The number is not cut: `darken_max` pulls it into nothing (its
            // text bone, the jackpot word's pull from `double_value`).
            cell.darken({ max: true });
          }),
        ),
      // The scarab revealing now, above everything (cell.setRevealing).
      setRevealing: (col, row, on) => cells.current?.[col]?.[row]?.setRevealing?.(on),
      // Being hit by a scarab now, over its neighbours (cell.setHit).
      setHit: (col, row, on) => cells.current?.[col]?.[row]?.setHit?.(on),
      // Dimmed while a scarab works elsewhere (cell.setDimmed).
      setDimmed: (col, row, on) => !!cells.current?.[col]?.[row]?.setDimmed?.(on),
      // A value landed in a chest: its collect jiggle (cell.playCollect).
      playCollect: (col, row) => cells.current?.[col]?.[row]?.playCollect?.(),
      // A silver chest that has made its one collection shuts (cell.closeIfSpent).
      // closeIfSpent: (col, row) => cells.current?.[col]?.[row]?.closeIfSpent?.(),
      // `final`: the respin that settles the round — the golden chest shuts too.
      closeIfSpent: (col, row, final = false) =>
        cells.current?.[col]?.[row]?.closeIfSpent?.(final),
      // Every chest still open shuts — the round is over (End Collector start).
      closeOpenChests: () =>
        cells.current?.forEach((column) => column.forEach((c) => c?.closeIfSpent?.(true))),
      // A scarab multiplied a chest (cell.playChestMultiplied).
      playChestMultiplied: (col, row) =>
        cells.current?.[col]?.[row]?.playChestMultiplied?.() ?? Promise.resolve(),
      // The scarab's glow alone behind a cell (cell.playMultiplyGlow).
      playMultiplyGlow: (col, row) => cells.current?.[col]?.[row]?.playMultiplyGlow?.(),
      valueLabel: (col, row) => cells.current?.[col]?.[row]?.valueLabel() ?? null,
      // Resolves when the cell's landing clips have played out (cell.landed).
      landed: (col, row) => cells.current?.[col]?.[row]?.landed?.() ?? Promise.resolve(),
      trailLayer: () => trailLayerRef.current,
      // ── Locked rows ──
      // The rows past `unlockedRows` (data rows) under their bar, at once —
      // the room's first frame, a reload.
      // setUnlockedRows: (unlockedRows) => {
        // unlockedRef.current = unlockedRows;
        // lockBars.current.forEach((bar, screenRow) => {
          // anime.remove(bar);
          // bar.alpha = toDataRow(screenRow) >= unlockedRows ? LOCKED_ALPHA : 0;
        // });
      // },
      // // The rows from `from` to `to` (data rows) open: their bars fade away.
      // // Resolves when the fade is done.
      // unlockRows: (from, to) => {
        // unlockedRef.current = to;
        // const opening = lockBars.current.filter((_, screenRow) => {
          // const d = toDataRow(screenRow);
          // return d >= from && d < to;
        // });
        // if (!opening.length) return Promise.resolve();
        // return new Promise((resolve) => {
          // opening.forEach((bar) => anime.remove(bar));
          // anime({
            // targets: opening,
            // alpha: 0,
            // duration: UNLOCK_FADE_MS,
            // easing: "easeInQuad",
            // complete: () => resolve(),
          // });
        // });
      // },
      cellHeight: () => getPixelCellHeight?.() ?? cellHeight,
      // The lock plates' layer (see platesLayerRef).
      platesLayer: () => platesLayerRef.current,
      // A plate's `out` above every cell (see PLATES_OPENING_Z).
      platesOpeningLayer: () => platesOpeningLayerRef.current,
      // ── The last cells' anticipation (HoldAndWinBoard.land's chain) ──
      playCellAnticipation: (col, row) => {
        const anchor = containerRefs.current[col]?.[row]?.current;
        if (!anchor) return;
        anticipation.current?.play(anchor.x, anchor.y, cellWidth, cellHeight);
      },
      stopCellAnticipation: () => anticipation.current?.stop(),
      hideCellAnticipation: () => anticipation.current?.hide(),
      cellAnticipationTiming: () =>
        anticipation.current?.timing() ?? { inMs: 0, loopMs: 1000, outMs: 0 },
      // Cells in the active zone (the bottom `unlockedRows` screen rows) draw
      // OVER the plates, the rest UNDER them.
      setOpenRows: (unlockedRows) => {
        cells.current?.forEach((column) =>
          column.forEach((cell, screenRow) =>
            cell?.setBaseZ(
              // toDataRow(screenRow) < unlockedRows ? CELL_Z_OPEN : CELL_Z_LOCKED,
              isScreenRowOpen(screenRow, unlockedRows) ? CELL_Z_OPEN : CELL_Z_LOCKED,
            ),
          ),
        );
      },
    }),
    // [cellHeight],
    [cellHeight, cellWidth, getPixelCellHeight],
  );

  const left = -((HOLD_AND_WIN_COLUMNS - 1) / 2) * cellWidth;
  const top = -((HOLD_AND_WIN_ROWS - 1) / 2) * cellHeight;

  return (
    <Container name="hold_and_win_overlay" sortableChildren>
      {Array.from({ length: HOLD_AND_WIN_COLUMNS }, (_, c) =>
        Array.from({ length: HOLD_AND_WIN_ROWS }, (_, r) => (
          <Container
            key={`hnw_ov_${c}_${r}`}
            ref={containerRefs.current[c][r]}
            x={left + c * cellWidth}
            y={top + r * cellHeight}
          />
        )),
      )}
      {/* The lock plates — between the locked rows' cells and the open
          rows' (setOpenRows). */}
      <Container
        ref={platesLayerRef}
        name="hold_and_win_lock_plates"
        zIndex={PLATES_Z}
      />
      {/* A plate playing its `out` — above every cell (PLATES_OPENING_Z). */}
      <Container
        ref={platesOpeningLayerRef}
        name="hold_and_win_lock_plates_opening"
        zIndex={PLATES_OPENING_Z}
      />
      {/* Above every cell, below the trails. */}
      {/* The black lock bars — replaced by the room's locked_reel plates
          (HoldAndWinGameViewController.attachLockedRows, 2026-09-30).
      <Container
        ref={lockLayerRef}
        name="hold_and_win_lock_layer"
        zIndex={500}
      />
      */}
      <Container
        ref={anticipationLayerRef}
        name="hold_and_win_cell_anticipation"
        zIndex={ANTICIPATION_Z}
      />
      <Container
        ref={trailLayerRef}
        name="hold_and_win_trail_layer"
        zIndex={1000}
        sortableChildren
      />
    </Container>
  );
  },
);

HoldAndWinOverlay.displayName = "HoldAndWinOverlay";

export default memo(HoldAndWinOverlay);
